import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiUsers, FiPlus, FiPhone, FiSearch, FiMoreVertical,
  FiFileText, FiMessageCircle, FiBook, FiX, FiSave, FiUser, FiFilter, FiEdit2, FiTrash2, FiUpload, FiAward
} from 'react-icons/fi';
import { FaLine } from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import { agentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AgentProfileDetail from '../../components/agents/AgentProfileDetail';
import ImportAgentsModal from '../../components/agents/ImportAgentsModal';
import ImportAgentsModalV2 from '../../components/agents/ImportAgentsModalV2';
import { AGENT_STATUS, AGENT_STATUS_OPTIONS, getAgentStatusMeta } from '../../utils/agentStatus';

const LineClients = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredClientId, setFilteredClientId] = useState(null);
  
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportV2Open, setIsImportV2Open] = useState(false);

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    fetchClients(); // Refresh data on close
  };

  // Form State (Create)
  const initialFormState = {
    firstName: '',
    lastName: '',
    nickname: '',
    phone: '',
    lineId: '',
    position: '',
    agentCode: '',
    agentStatus: AGENT_STATUS.ACTIVE,
  };
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const clientIdParam = searchParams.get('clientId');
    if (clientIdParam) {
      setFilteredClientId(parseInt(clientIdParam));
    } else {
      setFilteredClientId(null);
    }
  }, [searchParams]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data } = await agentAPI.getAgents();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('ไม่สามารถดึงข้อมูลลูกทีมได้');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // If user manually searches, clear the param-based filter
    if (filteredClientId) setFilteredClientId(null);
  };

  const clearFilter = () => {
    setFilteredClientId(null);
    setSearchTerm('');
    navigate('/line/clients', { replace: true });
  };

  const filteredClients = clients.filter(client => {
    // Priority 1: ID Filter from params
    if (filteredClientId) {
      return client.id === filteredClientId;
    }

    // Priority 2: Text Search
    const search = searchTerm.toLowerCase();
    const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
    const nickname = (client.nickname || '').toLowerCase();
    const phone = (client.phone || '').toLowerCase();
    const agentCode = (client.agentCode || '').toLowerCase();
    
    const matchesSearch = fullName.includes(search) ||
           nickname.includes(search) ||
           phone.includes(search) ||
           agentCode.includes(search);
    const matchesStatus = filterStatus === 'all' || getAgentStatusMeta(client.agentStatus).status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.agentCode) {
      toast.error('กรุณากรอกข้อมูลที่จำเป็น (ชื่อ, นามสกุล, รหัสตัวแทน)');
      return;
    }

    setSaving(true);
    try {
      await agentAPI.createAgent(formData);
      toast.success('เพิ่มลูกทีมเรียบร้อยแล้ว');
      setIsModalOpen(false);
      setFormData(initialFormState);
      fetchClients();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('ไม่สามารถเพิ่มลูกทีมได้: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = async (client) => {
    setIsEditModalOpen(true);
    setClientLoading(true);
    try {
      // Fetch full details
      const { data } = await agentAPI.getAgent(client.id);
      setSelectedClient(data);
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('ไม่สามารถดึงข้อมูลรายละเอียดได้');
      setSelectedClient(client); // Fallback to list data
    } finally {
      setClientLoading(false);
    }
  };

  const handleUpdateClient = async (updatedData) => {
    try {
      const dataToSave = {
        ...updatedData,
        monthlySales: updatedData.monthlySales ? parseFloat(updatedData.monthlySales) : 0,
        yearlySales: updatedData.yearlySales ? parseFloat(updatedData.yearlySales) : 0,
      };

      await agentAPI.updateAgent(selectedClient.id, dataToSave);
      
      // Update list state
      setClients(prevClients => 
        prevClients.map(c => c.id === selectedClient.id ? { ...c, ...dataToSave } : c)
      );
      
      setSelectedClient(dataToSave);
      toast.success('บันทึกข้อมูลเรียบร้อยแล้ว');
      return true;
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('ไม่สามารถบันทึกข้อมูลได้');
      return false;
    }
  };

  const handleDeleteClick = async (e, client) => {
    e.stopPropagation(); // Prevent modal from opening
    if (window.confirm(`คุณแน่ใจหรือไม่ที่จะลบ ${client.firstName} ${client.lastName}?`)) {
      try {
        await agentAPI.deleteAgent(client.id);
        toast.success('ลบข้อมูลเรียบร้อยแล้ว');
        setClients(prev => prev.filter(c => c.id !== client.id));
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error('ไม่สามารถลบข้อมูลได้');
      }
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">ลูกทีมของฉัน</h1>
            <p className="text-gray-600 text-sm">จัดการข้อมูลทีมงานและตัวแทนในสังกัด</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">นำเข้าข้อมูล</span>
              <span className="sm:hidden">นำเข้า</span>
            </button>
            <button
              onClick={() => setIsImportV2Open(true)}
              className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">นำเข้า (รูปแบบทางการ)</span>
              <span className="sm:hidden">ทางการ</span>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">เพิ่มลูกทีม</span>
              <span className="sm:hidden">เพิ่ม</span>
            </button>
          </div>
        </div>

        {/* Search Bar & Filter Status */}
        <div className="space-y-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="ค้นหาตามชื่อ, ชื่อเล่น, เบอร์โทร, หรือรหัสตัวแทน..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="all">สถานะทั้งหมด</option>
            {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          
          {filteredClientId && (
             <div className="flex items-center justify-between bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-100">
                <div className="flex items-center text-indigo-700 text-sm">
                   <SafeIcon icon={FiFilter} className="w-4 h-4 mr-2" />
                   <span>แสดงผลเฉพาะรายการที่เลือก</span>
                </div>
                <button 
                   onClick={clearFilter}
                   className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline"
                >
                   ล้างตัวกรอง
                </button>
             </div>
          )}
        </div>
      </div>

      {/* Client List */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.length > 0 ? (
            filteredClients.map((client) => (
              <div 
                key={client.id} 
                onClick={() => handleEditClick(client)}
                className={`bg-white rounded-xl shadow-sm border border-l-4 hover:shadow-md transition-shadow overflow-hidden cursor-pointer ${filteredClientId === client.id ? 'ring-2 ring-indigo-200' : ''} ${getAgentStatusMeta(client.agentStatus).border}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {client.user?.pictureUrl ? (
                          <img className="h-12 w-12 rounded-full object-cover border-2 border-indigo-100" src={client.user.pictureUrl} alt="" />
                        ) : (
                          <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center border-2 border-indigo-100">
                            <SafeIcon icon={FiUser} className="w-6 h-6 text-indigo-500" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-1">
                          {client.firstName} {client.lastName}
                        </h3>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                          <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600 font-medium">
                            {client.nickname || '-'}
                          </span>
                          <span className="text-gray-300">|</span>
                          <span>{client.agentCode}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button 
                        onClick={(e) => handleDeleteClick(e, client)}
                        className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                        title="ลบข้อมูล"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-gray-400" />
                      {client.phone || '-'}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FaLine} className="w-4 h-4 mr-2 text-green-500" />
                      {client.lineId || '-'}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <SafeIcon icon={FiAward} className="w-4 h-4 mr-2 text-gray-400" />
                      {client.position || 'ตัวแทน'}
                    </div>
                    {client.motivation && (
                      <div className="flex items-start text-sm text-gray-600">
                         <span className="w-4 h-4 mr-2 flex justify-center text-gray-400 font-bold text-xs">M</span>
                         <span className="line-clamp-2 text-xs">{client.motivation}</span>
                      </div>
                    )}
                     {client.interest && (
                      <div className="flex items-start text-sm text-gray-600">
                         <span className="w-4 h-4 mr-2 flex justify-center text-gray-400 font-bold text-xs">I</span>
                         <span className="line-clamp-2 text-xs">{client.interest}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600">
                    <div>
                      <span className="text-gray-400 block">เลขที่ใบอนุญาต:</span>
                      <span>{client.licenseNumber || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">รหัสหน่วย:</span>
                      <span>{client.unitCode || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">หมดอายุใบอนุญาต:</span>
                      <span>{client.licenseExpiryDate ? new Date(client.licenseExpiryDate).toLocaleDateString('th-TH') : '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">วันที่เริ่มสัญญา:</span>
                      <span>{client.joinDate ? new Date(client.joinDate).toLocaleDateString('th-TH') : '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400 block mb-1">สถานภาพ:</span>
                      <span className={`inline-flex px-2 py-1 rounded-full font-medium ${getAgentStatusMeta(client.agentStatus).badge}`}>
                        {getAgentStatusMeta(client.agentStatus).label}
                      </span>
                    </div>
                    {client.email && (
                      <div className="col-span-2">
                        <span className="text-gray-400 block">อีเมล์:</span>
                        <span>{client.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions Divider */}
                  <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/line/policies?clientId=${client.id}`);
                      }}
                      className="flex flex-col items-center justify-center p-2 rounded hover:bg-gray-50 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <SafeIcon icon={FiFileText} className="w-5 h-5 mb-1" />
                      <span className="text-xs font-bold text-gray-900">{client.policies?.length || 0}</span>
                      <span className="text-[10px]">กรมธรรม์</span>
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/line/coaching?clientId=${client.id}`);
                      }}
                      className="flex flex-col items-center justify-center p-2 rounded hover:bg-gray-50 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <SafeIcon icon={FiMessageCircle} className="w-5 h-5 mb-1" />
                      <span className="text-xs font-bold text-gray-900">{client.coachingLogs?.length || 0}</span>
                      <span className="text-[10px]">การโค้ช</span>
                    </button>
                    <button 
                       onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/line/training?clientId=${client.id}`);
                      }}
                      className="flex flex-col items-center justify-center p-2 rounded hover:bg-gray-50 text-gray-600 hover:text-indigo-600 transition-colors"
                    >
                      <SafeIcon icon={FiBook} className="w-5 h-5 mb-1" />
                      <span className="text-xs font-bold text-gray-900">{client.trainings?.length || 0}</span>
                      <span className="text-[10px]">อบรม</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full py-10 text-center text-gray-500 bg-white rounded-lg border border-gray-100">
              <SafeIcon icon={FiUsers} className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm font-medium">
                 {filteredClientId ? 'ไม่พบข้อมูลลูกทีมที่เลือก' : 'ไม่พบข้อมูลลูกทีม'}
              </p>
              <p className="text-xs text-gray-400">
                 {filteredClientId ? 'อาจถูกลบไปแล้ว' : 'ลองค้นหาด้วยคำอื่น หรือเพิ่มลูกทีมใหม่'}
              </p>
              {filteredClientId && (
                 <button onClick={clearFilter} className="mt-3 text-indigo-600 text-sm font-medium hover:underline">
                    ดูทั้งหมด
                 </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Client Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative z-10 bg-white rounded-lg text-left overflow-hidden shadow-xl w-full max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    เพิ่มลูกทีมใหม่
                  </h3>
                  <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                    <SafeIcon icon={FiX} className="h-6 w-6" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ชื่อจริง <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">นามสกุล <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ชื่อเล่น</label>
                      <input
                        type="text"
                        name="nickname"
                        value={formData.nickname}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">รหัสตัวแทน <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="agentCode"
                        required
                        value={formData.agentCode}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">ตำแหน่ง</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="เช่น FA, Unit Manager"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">สถานภาพตัวแทน</label>
                    <select
                      name="agentStatus"
                      value={formData.agentStatus}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">เบอร์โทรศัพท์</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Line ID</label>
                      <input
                        type="text"
                        name="lineId"
                        value={formData.lineId}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
      )}

      {/* Edit Client Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" onClick={handleCloseEditModal}></div>
          <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
              <div className="bg-white px-4 py-3 border-b flex justify-between items-center sticky top-0 z-10">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  แก้ไขข้อมูลลูกทีม
                </h3>
                <button onClick={handleCloseEditModal} className="text-gray-400 hover:text-gray-500">
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {clientLoading ? (
                  <LoadingSpinner />
                ) : (
                  <AgentProfileDetail profile={selectedClient} onSave={handleUpdateClient} />
                )}
              </div>
            </div>
          </div>
      )}

      <ImportAgentsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => { setIsImportModalOpen(false); fetchClients(); toast.success('นำเข้าข้อมูลเรียบร้อยแล้ว'); }}
      />
      <ImportAgentsModalV2
        isOpen={isImportV2Open}
        onClose={() => setIsImportV2Open(false)}
        onSuccess={() => { setIsImportV2Open(false); fetchClients(); toast.success('นำเข้าข้อมูลเรียบร้อยแล้ว'); }}
      />
    </div>
  );
};

export default LineClients;
